import { X, Download, Film } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useToast } from "@/features/toast";
import { miniAppBridgeService } from "@/api/services";

interface VideoViewerProps {
  video: string;
  onClose: () => void;
  messageId?: string;
  title?: string;
}

export function VideoViewer({ video, onClose, title }: VideoViewerProps) {
  const { t } = useTranslation();
  const { success: successToast, error: errorToast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // ESC ile kapat + scroll lock
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [onClose]);

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const urlLower = video.toLowerCase();
      let extension = "mp4";
      if (urlLower.includes(".webm")) extension = "webm";
      else if (urlLower.includes(".avi")) extension = "avi";
      else if (urlLower.includes(".mov")) extension = "mov";
      else if (urlLower.includes(".mkv")) extension = "mkv";

      const response = await fetch(video);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `video-${Date.now()}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      successToast(t("wizard.downloadStarted") || "İndirme başlatıldı");
    } catch {
      window.open(video, "_blank");
      errorToast(t("wizard.downloadFailed") || "İndirme başarısız");
    } finally {
      setIsDownloading(false);
    }
  };

  const displayTitle = title || video.split("/").pop() || "video";

  const modalContent = (
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        key="video-viewer-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-8"
        style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(12px)" }}
        onClick={onClose}
      >
        {/* Modal kapsayıcı */}
        <motion.div
          key="video-viewer-content"
          initial={{ scale: 0.93, opacity: 0, y: 12 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.93, opacity: 0, y: 12 }}
          transition={{ type: "spring", stiffness: 320, damping: 30 }}
          className="relative flex flex-col w-full max-w-5xl rounded-2xl overflow-hidden shadow-2xl"
          style={{ border: "1px solid rgba(255,255,255,0.10)", maxHeight: "90vh" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── Başlık çubuğu ── */}
          <div
            className="flex items-center justify-between px-4 py-3 flex-shrink-0"
            style={{ background: "rgba(15,15,20,0.95)" }}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-white/8 flex-shrink-0">
                <Film className="w-3.5 h-3.5 text-white/60" />
              </div>
              <span className="text-sm font-medium text-white/80 truncate">
                {displayTitle}
              </span>
            </div>

            {/* Sağ aksiyonlar */}
            <div className="flex items-center gap-2 flex-shrink-0 ml-3">
              {!miniAppBridgeService.isInMiniApp() && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDownload(); }}
                  disabled={isDownloading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 disabled:opacity-50"
                  style={{
                    background: isDownloading
                      ? "rgba(99,102,241,0.3)"
                      : "rgba(99,102,241,0.85)",
                    color: "#fff",
                    border: "1px solid rgba(129,140,248,0.4)",
                  }}
                  onMouseEnter={(e) => {
                    if (!isDownloading)
                      (e.currentTarget as HTMLButtonElement).style.background =
                        "rgba(99,102,241,1)";
                  }}
                  onMouseLeave={(e) => {
                    if (!isDownloading)
                      (e.currentTarget as HTMLButtonElement).style.background =
                        "rgba(99,102,241,0.85)";
                  }}
                >
                  {isDownloading ? (
                    <>
                      <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      <span>İndiriliyor...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-3.5 h-3.5" />
                      <span>{t("wizard.download") || "İndir"}</span>
                    </>
                  )}
                </button>
              )}

              <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                className="flex items-center justify-center w-7 h-7 rounded-lg transition-colors duration-150"
                style={{
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.10)",
                }}
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLButtonElement).style.background =
                    "rgba(255,255,255,0.15)")
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLButtonElement).style.background =
                    "rgba(255,255,255,0.08)")
                }
              >
                <X className="w-4 h-4 text-white/70" />
              </button>
            </div>
          </div>

          {/* ── Video alanı ──
              Native controls tamamen video içinde kalır;
              başlık/indirme çubuğu ALTA alındı, kontrollerin üzerine binmez.
          ── */}
          <div
            className="flex-1 min-h-0 overflow-hidden"
            style={{ background: "#000" }}
          >
            <video
              ref={videoRef}
              src={video}
              controls
              autoPlay
              playsInline
              className="w-full h-full object-contain"
              style={{ display: "block", maxHeight: "calc(90vh - 56px)" }}
            >
              {t("common.videoNotSupported", {
                defaultValue:
                  "Tarayıcınız video oynatmayı desteklemiyor.",
              })}
            </video>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
}
